import { Button } from "@/components/ui/UIElements/Button";
import React, { useState, useEffect } from "react";
import { Modal } from "../";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useCreateExtra, useUpdateExtra } from "../../../hooks/useProducts";

const CreateExtraModal = ({
  isOpen,
  onClose,
  businessId,
  extraToEdit = null,
}) => {
  const toast = useToast();
  const createMutation = useCreateExtra();
  const updateMutation = useUpdateExtra();
  const isEditMode = !!extraToEdit;

  const [formData, setFormData] = useState({
    name: "",
  });
  const [errors, setErrors] = useState({});

  // Initialize form data when extraToEdit changes
  useEffect(() => {
    if (extraToEdit) {
      setFormData({
        name: extraToEdit.name || "",
      });
    } else {
      setFormData({
        name: "",
      });
    }
    setErrors({});
  }, [extraToEdit, isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name?.trim()) {
      newErrors.name = "Name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!validateForm()) {
      toast.error("Please fix the errors");
      return;
    }

    try {
      const payload = {
        name: formData.name?.trim(),
      };

      if (isEditMode) {
        await updateMutation.mutateAsync({
          businessId,
          extraId: extraToEdit.id,
          payload,
        });
        handleApiSuccess("Extra updated successfully", toast);
      } else {
        await createMutation.mutateAsync({
          businessId,
          payload: { ...payload, business_id: businessId },
        });
        handleApiSuccess("Extra created successfully", toast);
      }
      handleClose();
    } catch (error) {
      handleApiError(error, toast, {
        title: isEditMode ? "Failed to Update Extra" : "Failed to Create Extra",
        defaultMessage: "Please try again.",
      });
    }
  };

  const handleClose = () => {
    setFormData({
      name: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode ? "Edit Extra" : "Create Extra"}
      subtitle={
        isEditMode ? "Update extra details" : "Add a new extra to this business"
      }
      maxWidth="max-w-md"
      className="ordering-catalog-surface"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-sm font-medium text-white">
            Name <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="border-border bg-card focus:ring-ring w-full rounded-lg border px-3 py-2 text-white placeholder-gray-500 focus:ring-2 focus:outline-none disabled:opacity-50"
            placeholder="Extra name"
            required
          />
          {errors.name && (
            <div className="text-destructive mt-1 text-xs">{errors.name}</div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <Button
            variant="ghost"
            type="button"
            onClick={handleClose}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="text-card-foreground bg-card border-border hover:bg-accent rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </Button>
          <Button
            variant="default"
            type="submit"
            disabled={
              createMutation.isPending ||
              updateMutation.isPending ||
              !formData.name.trim()
            }
            className="px-4 py-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isEditMode
              ? updateMutation.isPending
                ? "Updating..."
                : "Update Extra"
              : createMutation.isPending
                ? "Creating..."
                : "Create Extra"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateExtraModal;
